// DashboardSuperAdmin.jsx - Version Tailwind natif
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';

import { 
  Users, Shield, Cpu, HardDrive, Activity, 
  Zap, CheckCircle, XCircle, Clock,
  TrendingUp, TrendingDown, Database, KeyRound,
  RefreshCw, Loader2, UserCheck, UserX
} from 'lucide-react';
import { APP_CONFIG } from '../../config/appConfig';
import { apiClient } from '../../utils/api';

const StatCard = ({ title, value, icon: Icon, trend, trendValue, color }) => (
  <div className={`bg-white rounded-lg shadow-sm p-6 border-l-4 ${color || 'border-blue-500'} transition-all hover:shadow-md`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{title}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        {trend && (
          <div className={`flex items-center gap-1 mt-1 text-xs font-medium ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
            {trend === 'up' ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            <span>{trendValue}</span>
          </div>
        )}
      </div>
      <div className="p-3 rounded-lg bg-gray-50">
        {Icon && <Icon size={24} className="text-gray-600" />}
      </div>
    </div>
  </div>
);

const MetricProgress = ({ label, value, max, unit, color }) => {
  const percentage = Math.min((value / max) * 100, 100);
  const colorClasses = {
    primary: 'bg-blue-600',
    warning: 'bg-yellow-500',
    danger: 'bg-red-600'
  };
  
  return (
    <div className="space-y-2">
      <div className="flex justify-between text-xs font-medium">
        <span className="text-gray-600 uppercase">{label}</span>
        <span className="text-gray-800">{value}{unit} / {max}{unit}</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2.5">
        <div 
          className={`h-2.5 rounded-full ${colorClasses[color] || colorClasses.primary} transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

const AdminTable = ({ admins, loading }) => {
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="animate-spin text-gray-400" size={24} />
      </div>
    );
  }

  if (!admins || admins.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-4xl mb-3">👤</div>
        <h3 className="text-lg font-medium text-gray-900">Aucun administrateur</h3>
        <p className="text-sm text-gray-500 mt-1">Aucun administrateur n'a été trouvé dans le système.</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Administrateur</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rôle</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {admins.map(admin => (
            <tr key={admin.id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-800 font-bold text-xs">
                    {admin.nom?.[0] || admin.email?.[0] || 'A'}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{admin.nom || ''} {admin.prenom || ''}</p>
                    <p className="text-[9px] text-gray-400 font-mono">{admin.username || admin.email}</p>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{admin.email}</td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${admin.role === 'ROLE_SUPERADMIN' || admin.role === 'SUPERADMIN' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                  {admin.role?.replace('ROLE_', '') || 'ADMIN'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {admin.actif !== false ? (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircle size={10} /> Actif
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <XCircle size={10} /> Inactif
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const MoteurStatusCard = ({ name, status, icon: Icon }) => (
  <div className={`bg-white rounded-lg shadow-sm p-4 border-l-4 ${status ? 'border-green-500' : 'border-red-500'}`}>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${status ? 'bg-blue-50' : 'bg-red-50'}`}>
          {Icon && <Icon size={20} className={status ? 'text-blue-600' : 'text-red-600'} />}
        </div>
        <div>
          <p className="text-xs font-bold text-gray-700">{name}</p>
          <p className={`text-[9px] font-bold ${status ? 'text-green-600' : 'text-red-600'}`}>
            {status ? 'Opérationnel' : 'Hors ligne'}
          </p>
        </div>
      </div>
      {status ? (
        <CheckCircle size={18} className="text-green-500" />
      ) : (
        <XCircle size={18} className="text-red-500" />
      )}
    </div>
  </div>
);

export default function DashboardSuperAdmin() {
  const { token } = useAuth();
  const isMounted = useRef(true);
  const intervalRef = useRef(null);
  const [admins, setAdmins] = useState([]);
  const [stats, setStats] = useState({
    totalAdmins: 0,
    activeAdmins: 0,
    totalApiClients: 0,
    cpuUsage: 0,
    memoryUsage: 0,
    memoryTotal: 2048,
    uptime: '...'
  });
  const [moteurs, setMoteurs] = useState({
    orangeSms: false,
    mtnSms: false,
    moovSms: false,
    stribe: false
  });
  const [refreshing, setRefreshing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAdmins = useCallback(async () => {
    if (!isMounted.current || !token) return;
    try {
      const data = await apiClient('/api/v1/config/admins');
      if (isMounted.current) {
        let adminsList = [];
        if (data && data.data && Array.isArray(data.data)) {
          adminsList = data.data;
        } else if (Array.isArray(data)) {
          adminsList = data;
        } else if (data && data.content && Array.isArray(data.content)) {
          adminsList = data.content;
        }
        
        setAdmins(adminsList);
        setStats(prev => ({
          ...prev,
          totalAdmins: adminsList.length,
          activeAdmins: adminsList.filter(a => a.actif !== false).length
        }));
        setError(null);
      }
    } catch (error) {
      console.error("Erreur récupération admins:", error);
      setAdmins([]);
    }
  }, [token]);

  const fetchSystemStats = useCallback(async () => {
    if (!isMounted.current || !token) return;
    try {
      const data = await apiClient('/api/v1/config/monitor/stats');
      if (isMounted.current && data) {
        setStats(prev => ({
          ...prev,
          cpuUsage: parseFloat(data.cpuLoad?.replace('%', '') || 0),
          memoryUsage: parseInt(data.ramUsage?.replace(' MB', '').replace('MB', '') || 0),
          memoryTotal: parseInt(data.ramTotal?.replace(' MB', '').replace('MB', '') || 2048),
          uptime: data.uptime || '...'
        }));
        setError(null);
      }
    } catch (error) {
      console.error("Erreur récupération stats système:", error);
    }
  }, [token]);

  const fetchApiClients = useCallback(async () => {
    if (!isMounted.current || !token) return;
    try {
      const data = await apiClient('/api/v1/config/oauth2-clients');
      if (isMounted.current) {
        setStats(prev => ({ 
          ...prev, 
          totalApiClients: Array.isArray(data) ? data.length : 0 
        }));
        setError(null);
      }
    } catch (error) {
      console.error("Erreur récupération API clients:", error);
      setStats(prev => ({ ...prev, totalApiClients: 0 }));
    }
  }, [token]);

  const fetchMoteursStatus = useCallback(async () => {
    if (!isMounted.current || !token) return;
    try {
      const data = await apiClient('/api/v1/config/monitor/status');
      if (isMounted.current && data) {
        setMoteurs({
          orangeSms: data.orangeSms || false,
          mtnSms: data.mtnSms || false,
          moovSms: data.moovSms || false,
          stribe: data.stribe || false
        });
        setError(null);
      }
    } catch (error) {
      console.error("Erreur récupération statut moteurs:", error);
    }
  }, [token]);

  const refreshAll = useCallback(async () => {
    if (!isMounted.current) return;
    setRefreshing(true);
    setError(null);
    
    try {
      await Promise.all([
        fetchAdmins(),
        fetchSystemStats(),
        fetchApiClients(),
        fetchMoteursStatus()
      ]);
    } catch (error) {
      console.error("Erreur lors du rafraîchissement:", error);
    } finally {
      if (isMounted.current) {
        setRefreshing(false);
        setIsLoading(false);
      }
    }
  }, [fetchAdmins, fetchSystemStats, fetchApiClients, fetchMoteursStatus]);

  const startInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = setInterval(() => {
      if (isMounted.current && token) {
        fetchSystemStats();
        fetchMoteursStatus();
      }
    }, 30000);
  }, [fetchSystemStats, fetchMoteursStatus, token]);

  useEffect(() => {
    isMounted.current = true;
    
    const init = async () => {
      await refreshAll();
      if (isMounted.current) {
        startInterval();
      }
    };
    
    init();
    
    return () => {
      isMounted.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [refreshAll, startInterval]);

  const memoryPercentage = stats.memoryTotal > 0 ? (stats.memoryUsage / stats.memoryTotal) * 100 : 0;
  const cpuColor = stats.cpuUsage > 80 ? 'danger' : stats.cpuUsage > 60 ? 'warning' : 'primary';
  const memColor = memoryPercentage > 80 ? 'danger' : memoryPercentage > 60 ? 'warning' : 'primary';

  return (
    <div className="p-4 md:p-6 bg-gray-100 min-h-screen font-sans">
      <div className="max-w-8xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-serif font-black text-green-800 uppercase tracking-tighter">
              Tableau de Bord Super Administrateur
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Surveillance et gestion globale du système
            </p>
          </div>
          <button
            onClick={refreshAll}
            disabled={refreshing}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin mr-2' : 'mr-2'} />
            Rafraîchir
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
            <div className="flex items-center">
              <XCircle className="text-red-500 mr-2" size={20} />
              <p className="text-red-700 text-sm font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard 
            title="Administrateurs" 
            value={stats.totalAdmins}
            icon={Users}
            trend="up"
            trendValue={`${stats.activeAdmins} actifs`}
            color="border-indigo-500"
          />
          <StatCard 
            title="Clients API OAuth2" 
            value={stats.totalApiClients}
            icon={KeyRound}
            color="border-emerald-500"
          />
          <StatCard 
            title="Charge CPU" 
            value={`${stats.cpuUsage || 0}%`}
            icon={Cpu}
            trend={stats.cpuUsage > 70 ? 'up' : 'down'}
            trendValue={stats.cpuUsage > 70 ? 'Élevée' : 'Normale'}
            color="border-amber-500"
          />
          <StatCard 
            title="Mémoire RAM" 
            value={`${stats.memoryUsage || 0} MB`}
            icon={HardDrive}
            trend={memoryPercentage > 80 ? 'up' : 'down'}
            trendValue={`${Math.round(memoryPercentage)}% utilisé`}
            color="border-purple-500"
          />
        </div>

        {/* Metrics Progress */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4">
              <Activity size={18} /> Métriques Système
            </h3>
            <div className="space-y-6">
              <MetricProgress 
                label="Utilisation CPU"
                value={stats.cpuUsage || 0}
                max={100}
                unit="%"
                color={cpuColor}
              />
              <MetricProgress 
                label="Utilisation Mémoire"
                value={stats.memoryUsage || 0}
                max={stats.memoryTotal || 2048}
                unit=" MB"
                color={memColor}
              />
              <div className="pt-4 border-t border-gray-200">
                <div className="flex justify-between text-xs text-gray-500">
                  <span className="font-bold">Uptime serveur</span>
                  <span className="font-mono">{stats.uptime || '...'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Moteurs Status */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4">
              <Zap size={18} /> État des Moteurs
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <MoteurStatusCard name="Orange SMS" status={moteurs.orangeSms} icon={Zap} />
              <MoteurStatusCard name="MTN Mobile Money" status={moteurs.mtnSms} icon={Activity} />
              <MoteurStatusCard name="Moov Network" status={moteurs.moovSms} icon={Zap} />
              <MoteurStatusCard name="Stribe Payment" status={moteurs.stribe} icon={Activity} />
            </div>
          </div>
        </div>

        {/* Admin Table */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Shield size={18} /> Liste des Administrateurs
                </h3>
                <p className="text-sm text-gray-500">
                  {stats.activeAdmins} actif(s) sur {stats.totalAdmins} au total
                </p>
              </div>
              <div className="flex gap-2">
                <div className="flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-lg">
                  <UserCheck size={12} className="text-green-600" />
                  <span className="text-[9px] font-bold">Actifs: {stats.activeAdmins}</span>
                </div>
                <div className="flex items-center gap-1 bg-gray-100 px-3 py-1 rounded-lg">
                  <UserX size={12} className="text-red-600" />
                  <span className="text-[9px] font-bold">Inactifs: {stats.totalAdmins - stats.activeAdmins}</span>
                </div>
              </div>
            </div>
          </div>
          <AdminTable admins={admins} loading={isLoading && admins.length === 0} />
        </div>

        {/* Footer Info */}
        <div className="bg-green-800 rounded-2xl p-4 text-center shadow-lg">
          <div className="flex flex-wrap items-center justify-center gap-6 text-xs text-green-200">
            <div className="flex items-center gap-2">
              <Clock size={14} />
              <span>Dernière synchronisation: {new Date().toLocaleString('fr-FR')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Database size={14} />
              <span>API Clients actifs: {stats.totalApiClients}</span>
            </div>
            <div className="flex items-center gap-2">
              <Activity size={14} />
              <span>Moteurs opérationnels: {Object.values(moteurs).filter(v => v).length}/4</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}